// Framework-free One Tap lifecycle so it can be unit tested with node:test.
// It initializes Google Identity Services, prompts once, and cancels the prompt as soon as the
// person starts the email flow (focus or submit of an email field) or the page goes away.

const EMAIL_FIELD_SELECTOR = 'input[type="email"], input[name="user[email]"]'

export const isEmailField = (target) => Boolean(target?.matches?.(EMAIL_FIELD_SELECTOR))

export const formContainsEmailField = (form) => Boolean(form?.querySelector?.(EMAIL_FIELD_SELECTOR))

export default class GoogleOneTapSession {
  constructor ({ root, getGoogle, clientId, context, nonce, onCredential }) {
    this.root = root
    this.getGoogle = getGoogle
    this.clientId = clientId
    this.context = context
    this.nonce = nonce
    this.onCredential = onCredential
    this.cancelled = false
    this.prompted = false

    // Focus cancels the prompt, except the page's own autofocus on load; clicking into or typing
    // in an autofocused field still cancels.
    this.handleFocus = (event) => {
      if (isEmailField(event.target) && !event.target.autofocus) this.cancel()
    }
    this.handleInteraction = (event) => {
      if (isEmailField(event.target)) this.cancel()
    }
    this.handleSubmit = (event) => {
      if (formContainsEmailField(event.target)) this.cancel()
    }
  }

  listen () {
    this.root.addEventListener('focusin', this.handleFocus, true)
    this.root.addEventListener('pointerdown', this.handleInteraction, true)
    this.root.addEventListener('input', this.handleInteraction, true)
    this.root.addEventListener('submit', this.handleSubmit, true)
  }

  // Called once the GIS script has loaded.
  start () {
    const google = this.getGoogle()
    if (this.cancelled || this.prompted || !google?.accounts?.id) return false

    google.accounts.id.initialize({
      client_id: this.clientId,
      callback: (response) => {
        if (!this.cancelled && response?.credential) this.onCredential(response.credential)
      },
      use_fedcm_for_prompt: true,
      cancel_on_tap_outside: true,
      context: this.context,
      itp_support: true,
      nonce: this.nonce
    })
    google.accounts.id.prompt()
    this.prompted = true

    return true
  }

  cancel () {
    if (this.cancelled) return

    this.cancelled = true
    this.getGoogle()?.accounts?.id?.cancel()
  }

  stop () {
    this.root.removeEventListener('focusin', this.handleFocus, true)
    this.root.removeEventListener('pointerdown', this.handleInteraction, true)
    this.root.removeEventListener('input', this.handleInteraction, true)
    this.root.removeEventListener('submit', this.handleSubmit, true)
    this.cancel()
  }
}
